// Settings → Kunden (B2B-Shop)
// Admin/Innendienst legen externe Shop-Kunden an, verknüpfen sie mit einem
// Easybill-Kunden (für die Preisgruppe & Rechnungslegung) und setzen das
// Initial-Passwort direkt.
import { useEffect, useState, useCallback } from 'react';
import { authFetch } from '../../lib/auth-fetch';
import { Users, Plus, Trash2, Pencil, Search, Save, X, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import ShopWhitelistSection from './ShopWhitelistSection';

function ShopVisibilitySection({ customer, onSaved }) {
  const [mode, setMode] = useState(customer?.shopOverride?.mode || 'inherit');
  const [groups, setGroups] = useState([]);
  const [selected, setSelected] = useState(new Set(customer?.shopOverride?.allowedArticleGroupIds || []));
  // Zusatzrabatt pro Artikelgruppe (nur in "custom"-Modus wirksam).
  // Map: groupId(str) → prozent-string (leerer String = kein Rabatt).
  const initialDiscounts = () => {
    const out = {};
    const raw = customer?.shopOverride?.groupDiscounts || {};
    Object.entries(raw).forEach(([k, v]) => {
      if (Number(v) > 0) out[String(k)] = String(v);
    });
    return out;
  };
  const [discounts, setDiscounts] = useState(initialDiscounts);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Wenn der Parent den Customer austauscht (anderer Kunde geöffnet), oder
  // nach einem Save ein frisches Customer-Objekt liefert, mit der NEUEN
  // shopOverride-Konfiguration arbeiten. Sonst zeigt der Dialog beim
  // Wiederöffnen weiter die alte Auswahl.
  useEffect(() => {
    setMode(customer?.shopOverride?.mode || 'inherit');
    setSelected(new Set(customer?.shopOverride?.allowedArticleGroupIds || []));
    setDiscounts(initialDiscounts());
  }, [customer?.id, customer?.shopOverride?.mode,
      JSON.stringify(customer?.shopOverride?.allowedArticleGroupIds || []),
      JSON.stringify(customer?.shopOverride?.groupDiscounts || {})]);

  useEffect(() => {
    (async () => {
      const r = await authFetch('/api/shop/article-groups');
      if (r.ok) {
        const d = await r.json();
        setGroups(d.items || []);
      }
      setLoading(false);
    })();
  }, []);

  const toggle = (gid) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(gid)) {
        next.delete(gid);
        // Beim Abwählen einer Gruppe auch den Rabatt entfernen — Rabatt
        // ohne Freigabe der Gruppe ist sinnfrei.
        setDiscounts(prevD => {
          const copy = { ...prevD };
          delete copy[String(gid)];
          return copy;
        });
      } else {
        next.add(gid);
      }
      return next;
    });
  };

  const setGroupDiscount = (gid, val) => {
    const clean = String(val).replace(',', '.').replace(/[^0-9.]/g, '');
    setDiscounts(prev => {
      const copy = { ...prev };
      if (clean === '' || Number(clean) <= 0) delete copy[String(gid)];
      else copy[String(gid)] = clean;
      return copy;
    });
  };

  const save = async () => {
    setSaving(true);
    // Nur Rabatte für tatsächlich freigegebene Gruppen mitschicken.
    const cleanDiscounts = {};
    if (mode === 'custom') {
      Array.from(selected).forEach(gid => {
        const v = Number(discounts[String(gid)]);
        if (v > 0 && v <= 100) cleanDiscounts[String(gid)] = v;
      });
    }
    const r = await authFetch(`/api/customers/${customer.id}/shop-visibility`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        mode,
        allowedArticleGroupIds: mode === 'custom' ? Array.from(selected).map(x => parseInt(x, 10)) : [],
        groupDiscounts: cleanDiscounts,
      }),
    });
    setSaving(false);
    if (r.ok) {
      const discountCount = Object.keys(cleanDiscounts).length;
      toast.success(mode === 'custom'
        ? `Eigene Whitelist gespeichert (${selected.size} Gruppen${discountCount > 0 ? `, ${discountCount} mit Rabatt` : ''})`
        : 'Fällt jetzt auf die globale Shop-Whitelist zurück');
      if (onSaved) onSaved();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Speichern fehlgeschlagen');
    }
  };

  return (
    <div className="border-t border-gray-100 pt-4 mt-2" data-testid="shop-visibility-section">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="text-xs uppercase tracking-wide text-gray-500 font-semibold flex items-center gap-1.5">
            <ShoppingBag className="w-3.5 h-3.5" /> Shop-Sichtbarkeit
          </h3>
          <p className="text-[10px] text-gray-400 mt-0.5">
            Welche Easybill-Artikelgruppen sieht dieser Kunde im B2B-Shop?
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          className="inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] bg-[#9ec043] text-white hover:bg-[#7ca735] disabled:opacity-50"
          data-testid="btn-save-shop-visibility"
        >
          <Save className="w-3 h-3" />
          {saving ? 'Speichere …' : 'Speichern'}
        </button>
      </div>

      <div className="flex gap-2 text-[11px] mb-3">
        <label className={`flex-1 cursor-pointer border rounded p-2 ${mode === 'inherit' ? 'bg-[#9ec043]/10 border-[#9ec043]' : 'bg-white border-gray-200'}`}>
          <input
            type="radio"
            name="shop-visibility-mode"
            value="inherit"
            checked={mode === 'inherit'}
            onChange={() => setMode('inherit')}
            className="mr-1.5"
            data-testid="shop-vis-mode-inherit"
          />
          <strong>Standard</strong> — globale Whitelist verwenden
        </label>
        <label className={`flex-1 cursor-pointer border rounded p-2 ${mode === 'custom' ? 'bg-[#9ec043]/10 border-[#9ec043]' : 'bg-white border-gray-200'}`}>
          <input
            type="radio"
            name="shop-visibility-mode"
            value="custom"
            checked={mode === 'custom'}
            onChange={() => setMode('custom')}
            className="mr-1.5"
            data-testid="shop-vis-mode-custom"
          />
          <strong>Individuell</strong> — eigene Auswahl
        </label>
      </div>

      {mode === 'custom' && (
        loading ? (
          <div className="text-xs text-gray-400 italic">Lade Artikelgruppen aus Easybill …</div>
        ) : (
          <div className="max-h-64 overflow-auto border border-gray-100 rounded p-2 bg-gray-50">
            <div className="flex justify-between items-center mb-2 text-[10px] text-gray-500">
              <span>
                {selected.size} von {groups.length} Gruppen ausgewählt
                {Object.keys(discounts).length > 0 && (
                  <span className="ml-1 text-[#9ec043]">
                    · {Object.keys(discounts).length} mit Rabatt
                  </span>
                )}
              </span>
              <button
                onClick={() => setSelected(new Set(groups.map(g => g.id)))}
                className="text-[10px] text-[#9ec043] hover:underline"
              >
                Alle wählen
              </button>
            </div>
            <div className="space-y-1">
              {groups.map(g => {
                const isSelected = selected.has(g.id);
                const dv = discounts[String(g.id)] || '';
                return (
                  <div
                    key={g.id}
                    className={`flex items-center gap-2 text-[11px] px-1.5 py-1 rounded ${isSelected ? 'bg-white' : ''}`}
                    data-testid={`shop-vis-row-${g.id}`}
                  >
                    <label className="flex items-center gap-1.5 flex-1 cursor-pointer min-w-0">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggle(g.id)}
                        data-testid={`shop-vis-group-${g.id}`}
                      />
                      <span className="truncate">{g.name}</span>
                    </label>
                    <div className={`flex items-center gap-1 shrink-0 ${isSelected ? '' : 'opacity-40'}`}>
                      <input
                        type="text"
                        inputMode="decimal"
                        value={dv}
                        placeholder="0"
                        onChange={(e) => setGroupDiscount(g.id, e.target.value)}
                        disabled={!isSelected}
                        className="w-12 h-6 text-[11px] text-right border border-gray-200 rounded px-1 focus:outline-none focus:border-[#9ec043] disabled:bg-gray-100"
                        title="Kundenspezifischer Zusatzrabatt in Prozent — wird vom Easybill-Preis abgezogen"
                        data-testid={`shop-vis-discount-${g.id}`}
                      />
                      <span className="text-gray-400 text-[10px]">%</span>
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-[10px] text-gray-400 mt-2 italic">
              Rabatt = zusätzlicher Prozent-Abzug vom Preisgruppen-Preis (Easybill).
              Beispiel: 10 % bei EM540 → Kunde sieht 90 % seines Preisgruppen-Preises.
            </p>
          </div>
        )
      )}
    </div>
  );
}

const PRICE_LEVELS = [
  { value: '', label: 'Standardpreis' },
  ...Array.from({ length: 9 }, (_, i) => ({
    value: `SALEPRICE${i + 2}`,
    label: `Preisgruppe ${i + 2}`,
  })),
];

const emptyForm = () => ({
  email: '',
  name: '',
  password: '',
  easybillCustomerId: null,
  easybillCustomerName: '',
  salePriceLevel: '',
  customerNote: '',
});

export default function CustomersManagementTab() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [ebQuery, setEbQuery] = useState('');
  const [ebResults, setEbResults] = useState([]);
  const [ebSearching, setEbSearching] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    const r = await authFetch('/api/customers');
    if (r.ok) setCustomers(await r.json());
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setEbQuery(''); setEbResults([]);
    setShowForm(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      email: c.email || '',
      name: c.name || '',
      password: '',
      easybillCustomerId: c.easybillCustomerId || null,
      easybillCustomerName: c.easybillCustomerName || '',
      salePriceLevel: c.salePriceLevel || '',
      customerNote: c.customerNote || '',
    });
    setEbQuery(''); setEbResults([]);
    setShowForm(true);
  };

  const closeForm = () => { setShowForm(false); setEditing(null); };

  const searchEasybill = async () => {
    if (ebQuery.trim().length < 2) return;
    setEbSearching(true);
    const r = await authFetch(
      `/api/customers/easybill-search?q=${encodeURIComponent(ebQuery.trim())}`
    );
    setEbSearching(false);
    if (r.ok) {
      const d = await r.json();
      setEbResults(d.items || []);
    } else {
      toast.error('Easybill-Suche fehlgeschlagen');
      setEbResults([]);
    }
  };

  const linkEasybillCustomer = (eb) => {
    const displayName = eb.company || `${eb.firstName} ${eb.lastName}`.trim();
    setForm(prev => ({
      ...prev,
      easybillCustomerId: eb.id,
      easybillCustomerName: displayName,
      salePriceLevel: eb.salePriceLevel || prev.salePriceLevel,
      // Email/Name nur vorbefüllen, wenn noch leer
      email: prev.email || eb.email || '',
      name: prev.name || displayName || '',
    }));
    setEbResults([]);
    setEbQuery('');
  };

  const save = async () => {
    if (!editing) {
      if (!form.email || !form.password || !form.name) {
        toast.error('E-Mail, Name und Passwort sind Pflicht');
        return;
      }
      if (form.password.length < 8) {
        toast.error('Passwort muss min. 8 Zeichen lang sein');
        return;
      }
    }
    setSaving(true);
    const body = { ...form };
    if (editing && !body.password) delete body.password;
    const url = editing ? `/api/customers/${editing.id}` : '/api/customers';
    const r = await authFetch(url, {
      method: editing ? 'PUT' : 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    setSaving(false);
    if (r.ok) {
      toast.success(editing ? 'Kunde aktualisiert' : 'Kunde angelegt');
      closeForm();
      await load();
    } else {
      const d = await r.json().catch(() => ({}));
      toast.error(d.detail || 'Speichern fehlgeschlagen');
    }
  };

  const del = async (c) => {
    if (!window.confirm(`Kunde "${c.name || c.email}" wirklich löschen?`)) return;
    const r = await authFetch(`/api/customers/${c.id}`, { method: 'DELETE' });
    if (r.ok) { toast.success('Kunde gelöscht'); await load(); }
    else toast.error('Löschen fehlgeschlagen');
  };

  return (
    <div className="space-y-4" data-testid="customers-management-tab">
      <ShopWhitelistSection />

      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Users className="w-5 h-5 text-[#9ec043]" />
          <h2 className="text-lg font-semibold text-[#32373c]">B2B-Shop-Kunden</h2>
          <span className="text-xs text-gray-400">({customers.length})</span>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735]"
          data-testid="btn-add-customer"
        >
          <Plus className="w-3.5 h-3.5" /> Neuen Kunden anlegen
        </button>
      </div>

      {/* Liste */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-6 text-sm text-gray-400">Lade Kunden …</div>
        ) : customers.length === 0 ? (
          <div className="p-6 text-sm text-gray-400">
            Noch keine B2B-Shop-Kunden vorhanden. Lege einen neuen Kunden an,
            um ihm Zugang zum Shop zu geben.
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-gray-50 text-gray-500 uppercase tracking-wider">
              <tr>
                <th className="text-left px-3 py-2 font-medium">Name / E-Mail</th>
                <th className="text-left px-3 py-2 font-medium">Easybill</th>
                <th className="text-left px-3 py-2 font-medium">Preisgruppe</th>
                <th className="text-left px-3 py-2 font-medium">Hinweis</th>
                <th className="text-right px-3 py-2 font-medium">Aktionen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {customers.map(c => (
                <tr key={c.id} data-testid={`customer-row-${c.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-medium text-[#32373c]">{c.name || '–'}</div>
                    <div className="text-gray-400">{c.email}</div>
                  </td>
                  <td className="px-3 py-2">
                    {c.easybillCustomerId ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded bg-blue-50 text-blue-700 text-[11px]">
                        #{c.easybillCustomerId}
                        {c.easybillCustomerName && ` · ${c.easybillCustomerName}`}
                      </span>
                    ) : <span className="text-gray-300">–</span>}
                  </td>
                  <td className="px-3 py-2">
                    {c.salePriceLevel ? (
                      <span className="px-2 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px]">
                        {PRICE_LEVELS.find(p => p.value === c.salePriceLevel)?.label || c.salePriceLevel}
                      </span>
                    ) : <span className="text-gray-300">Standard</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-500 max-w-[300px] truncate" title={c.customerNote}>
                    {c.customerNote || '–'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    <button
                      onClick={() => openEdit(c)}
                      className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-[#9ec043]"
                      title="Bearbeiten"
                      data-testid={`btn-edit-customer-${c.id}`}
                    >
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={() => del(c)}
                      className="p-1 hover:bg-gray-100 rounded text-gray-500 hover:text-red-500 ml-1"
                      title="Löschen"
                      data-testid={`btn-delete-customer-${c.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Form-Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl border border-gray-200 w-full max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="customer-form-modal">
            <div className="p-4 border-b border-gray-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-[#32373c]">
                {editing ? `Kunde bearbeiten: ${editing.name || editing.email}` : 'Neuen B2B-Kunden anlegen'}
              </h3>
              <button onClick={closeForm} className="p-1 hover:bg-gray-100 rounded text-gray-400">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {/* Easybill-Suche */}
              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">
                  Easybill-Kunde verknüpfen
                </label>
                <div className="flex gap-1.5">
                  <input
                    value={ebQuery}
                    onChange={e => setEbQuery(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && searchEasybill()}
                    placeholder="Name, Firma oder Kundennummer …"
                    className="flex-1 px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                    data-testid="input-easybill-search"
                  />
                  <button
                    onClick={searchEasybill}
                    disabled={ebSearching || ebQuery.trim().length < 2}
                    className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-600 text-xs hover:bg-gray-200 disabled:opacity-50"
                    data-testid="btn-easybill-search"
                  >
                    <Search className="w-3.5 h-3.5" /> Suchen
                  </button>
                </div>
                {ebResults.length > 0 && (
                  <div className="mt-2 border border-gray-200 rounded-lg max-h-48 overflow-y-auto">
                    {ebResults.map(eb => (
                      <button
                        key={eb.id}
                        onClick={() => linkEasybillCustomer(eb)}
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 border-b border-gray-100 last:border-b-0 text-xs"
                        data-testid={`eb-result-${eb.id}`}
                      >
                        <div className="font-medium text-[#32373c]">
                          {eb.company || `${eb.firstName} ${eb.lastName}`.trim()}
                        </div>
                        <div className="text-gray-400">
                          #{eb.number} · ID {eb.id}
                          {eb.salePriceLevel && ` · ${eb.salePriceLevel}`}
                          {eb.email && ` · ${eb.email}`}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {form.easybillCustomerId && (
                  <div className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-50 text-blue-700 text-xs">
                    Verknüpft: <strong>{form.easybillCustomerName}</strong>
                    <span className="text-blue-500/70">(ID {form.easybillCustomerId})</span>
                    <button
                      onClick={() => setForm(p => ({ ...p, easybillCustomerId: null, easybillCustomerName: '' }))}
                      className="ml-1 hover:text-red-500"
                      data-testid="btn-unlink-easybill"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">E-Mail (Login)</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                    disabled={!!editing}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                    data-testid="input-customer-email"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">Name</label>
                  <input
                    value={form.name}
                    onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                    data-testid="input-customer-name"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">
                    {editing ? 'Neues Passwort (leer = unverändert)' : 'Passwort (min. 8 Zeichen)'}
                  </label>
                  <input
                    type="password"
                    value={form.password}
                    onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                    data-testid="input-customer-password"
                  />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">Preisgruppe</label>
                  <select
                    value={form.salePriceLevel}
                    onChange={e => setForm(p => ({ ...p, salePriceLevel: e.target.value }))}
                    className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs bg-white focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                    data-testid="select-sale-price-level"
                  >
                    {PRICE_LEVELS.map(p => (
                      <option key={p.value} value={p.value}>{p.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-[11px] uppercase tracking-wide text-gray-500 font-medium mb-1.5">Interne Notiz</label>
                <textarea
                  value={form.customerNote}
                  onChange={e => setForm(p => ({ ...p, customerNote: e.target.value }))}
                  rows={2}
                  className="w-full px-3 py-1.5 border border-gray-200 rounded-lg text-xs resize-none focus:outline-none focus:ring-1 focus:ring-[#9ec043]"
                  placeholder="z.B. Sonderkonditionen, Ansprechpartner, Liefer­hinweise …"
                  data-testid="input-customer-note"
                />
              </div>

              {editing && <ShopVisibilitySection customer={editing} onSaved={async () => {
                await load();
                // editing-Objekt nach reload aktualisieren, sodass es die
                // frische shopOverride-Information enthält.
                const r = await authFetch('/api/customers');
                if (r.ok) {
                  const list = await r.json();
                  const updated = list.find(x => x.id === editing.id);
                  if (updated) setEditing(updated);
                }
              }} />}
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2">
              <button
                onClick={closeForm}
                className="px-3 py-1.5 rounded-lg text-xs text-gray-500 hover:bg-gray-100"
              >
                Abbrechen
              </button>
              <button
                onClick={save}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#9ec043] text-white text-xs font-medium hover:bg-[#7ca735] disabled:opacity-50"
                data-testid="btn-save-customer"
              >
                <Save className="w-3.5 h-3.5" />
                {saving ? 'Speichere …' : editing ? 'Aktualisieren' : 'Anlegen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
